/**
 * IntegrityTab - Vote hash verification, election cloning, soft delete, system test results.
 */
import React, { useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Chip, CircularProgress,
  Alert, Snackbar, Grid, Divider, LinearProgress, Tooltip,
} from "@mui/material";
import VerifiedUserIcon  from "@mui/icons-material/VerifiedUser";
import ContentCopyIcon   from "@mui/icons-material/ContentCopy";
import DeleteIcon        from "@mui/icons-material/Delete";
import RefreshIcon       from "@mui/icons-material/Refresh";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ErrorIcon         from "@mui/icons-material/Error";
import api from "../../utils/api";

const cardSx = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const btnSx  = { borderRadius:"10px", textTransform:"none", fontWeight:700, boxShadow:"none" };

const IntegrityTab = ({ election }) => {
  const [integrity,  setIntegrity]  = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [cloning,    setCloning]    = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [snack,      setSnack]      = useState({ open:false, msg:"", sev:"success" });

  const showSnack = (msg, sev="success") => setSnack({ open:true, msg, sev });

  const handleVerify = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/elections/${election.id}/verify-integrity`);
      setIntegrity(r.data);
    } catch { showSnack("Failed to verify vote integrity.", "error"); }
    finally { setLoading(false); }
  };

  const handleClone = async () => {
    const title = window.prompt("Enter a title for the cloned election:", `[CLONE] ${election.title}`);
    if (!title) return;
    setCloning(true);
    try {
      const r = await api.post(`/elections/${election.id}/clone`, { new_title: title });
      showSnack(`Election cloned (ID: ${r.data.id}). Find it in the election list.`);
    } catch { showSnack("Failed to clone election.", "error"); }
    finally { setCloning(false); }
  };

  const handleSoftDelete = async () => {
    if (!window.confirm("Archive this election? It will be hidden from the list but all data is preserved.")) return;
    setDeleting(true);
    try {
      await api.delete(`/elections/${election.id}/soft`);
      showSnack("Election archived. Refresh the list to see changes.");
    } catch { showSnack("Failed to archive election.", "error"); }
    finally { setDeleting(false); }
  };

  const pct = integrity ? Math.round((integrity.intact / Math.max(integrity.total, 1)) * 100) : 0;

  return (
    <Box>
      {/* Vote Integrity Verification */}
      <Paper elevation={0} sx={cardSx}>
        <Box sx={{ display:"flex", alignItems:"center", gap:1, mb:1.5 }}>
          <VerifiedUserIcon sx={{ color:"#2f63ad" }} />
          <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142" }}>Vote Hash Integrity</Typography>
        </Box>
        <Typography variant="body2" sx={{ color:"#6b778c", mb:2 }}>
          Verify that no votes have been tampered with since submission. Each vote has a SHA-256 hash
          computed at submission time - this tool recomputes them and checks for discrepancies.
        </Typography>

        <Button variant="contained" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <VerifiedUserIcon />}
          onClick={handleVerify} disabled={loading}
          sx={{ ...btnSx, bgcolor:"#2f63ad", "&:hover":{ bgcolor:"#1f4f94" }, mb:2 }}>
          {loading ? "Verifying..." : "Run Integrity Check"}
        </Button>

        {integrity && (
          <Box>
            <Box sx={{ display:"flex", gap:3, mb:2, flexWrap:"wrap" }}>
              {[
                { label:"Total Votes", value:integrity.total, color:"#223142" },
                { label:"Intact", value:integrity.intact, color:"#16a34a" },
                { label:"Tampered", value:integrity.tampered, color:"#dc2626" },
                { label:"Missing Hash", value:integrity.missing, color:"#b45309" },
              ].map(({ label, value, color }) => (
                <Box key={label} sx={{ textAlign:"center" }}>
                  <Typography variant="h5" sx={{ fontWeight:800, color }}>{value}</Typography>
                  <Typography variant="caption" sx={{ color:"#6b778c" }}>{label}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mb:2 }}>
              <Box sx={{ display:"flex", justifyContent:"space-between", mb:0.5 }}>
                <Typography variant="caption" sx={{ fontWeight:700, color:"#223142" }}>Integrity Score</Typography>
                <Typography variant="caption" sx={{ color: pct === 100 ? "#16a34a" : "#dc2626", fontWeight:700 }}>{pct}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct}
                sx={{ height:8, borderRadius:4, bgcolor:"rgba(0,0,0,0.06)", "& .MuiLinearProgress-bar":{ bgcolor: pct===100?"#16a34a":"#dc2626" } }} />
            </Box>

            {integrity.tampered === 0 && integrity.missing === 0 ? (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ borderRadius:2 }}>
                All {integrity.total} votes are intact. No tampering detected. Verified at {new Date(integrity.verified_at).toLocaleString()}.
              </Alert>
            ) : (
              <Alert severity="error" icon={<ErrorIcon />} sx={{ borderRadius:2 }}>
                {integrity.tampered} vote(s) may have been tampered with. Contact your system administrator immediately.
                {integrity.issues.length > 0 && (
                  <Box sx={{ mt:1 }}>
                    {integrity.issues.map(i => (
                      <Typography key={i.voteId} variant="caption" sx={{ display:"block", fontFamily:"monospace" }}>
                        Vote #{i.voteId}: stored={i.expected?.slice(0,12)}... computed={i.computed?.slice(0,12)}...
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      {/* Election Management Actions */}
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle1" sx={{ fontWeight:700, color:"#223142", mb:1.5 }}>Election Actions</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p:2, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.03)", border:"1px solid rgba(47,99,173,0.1)" }}>
              <Typography variant="body2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>Clone Election</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:1.5 }}>
                Create a new election with the same questions, options, and settings. Start fresh with new voting dates.
              </Typography>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={handleClone} disabled={cloning}
                sx={{ ...btnSx, borderColor:"rgba(47,99,173,0.3)", color:"#2f63ad" }}>
                {cloning ? "Cloning..." : "Clone This Election"}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p:2, borderRadius:"10px", bgcolor:"rgba(220,38,38,0.02)", border:"1px solid rgba(220,38,38,0.1)" }}>
              <Typography variant="body2" sx={{ fontWeight:700, color:"#223142", mb:0.5 }}>Archive Election</Typography>
              <Typography variant="caption" sx={{ color:"#6b778c", display:"block", mb:1.5 }}>
                Hide this election from the list. All votes, questions, and history are preserved for compliance.
              </Typography>
              <Button variant="outlined" startIcon={<DeleteIcon />} onClick={handleSoftDelete} disabled={deleting}
                sx={{ ...btnSx, borderColor:"rgba(220,38,38,0.3)", color:"#dc2626" }}>
                {deleting ? "Archiving..." : "Archive Election"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={()=>setSnack(s=>({...s,open:false}))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrityTab;
